"use client";

import { useCallback, useState } from "react";
import type { Sound } from "@/lib/supabase";
import { soundsApi } from "@/lib/supabase";
import { useDownload } from "@/hooks/useDownload";
import { recordPostDownloadConfirmation } from "@/lib/post-download-confirmation";
import {
  trackDownloadButtonClick,
  trackDownloadInitiated,
  trackDownloadCompleted,
} from "@/lib/tracking";

/**
 * Card download state + handler. Preserves the original call order:
 * tracking (click → initiated) → download-count increment → file download,
 * with completion tracking and the card's inline completion state on success.
 */
export function useCardDownload(sound: Sound, previewCompleted: boolean) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadDone, setDownloadDone] = useState(false);
  const { download: downloadFile } = useDownload();

  const handleCardDownload = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isDownloading || downloadDone) return;
      // W3-1: attribute download to the completion CTA placement when it
      // was the preview reaching ≥80% that surfaced the button prominently.
      const dlSource = previewCompleted ? 'preview_completion_cta' : 'sound_card';
      trackDownloadButtonClick({ sound_id: sound.id, sound_title: sound.title, category: sound.category, source: dlSource });
      trackDownloadInitiated({ sound_id: sound.id, sound_title: sound.title, category: sound.category, source: dlSource });
      setIsDownloading(true);
      try {
        await soundsApi.incrementDownloads(sound.id);
        await downloadFile({
          fileUrl: sound.file_url,
          soundId: sound.id,
          onSuccess: () => {
            trackDownloadCompleted({ sound_id: sound.id, sound_title: sound.title, category: sound.category, source: dlSource });
            setDownloadDone(true);
            setTimeout(() => setDownloadDone(false), 5000);
            // Arms /guide attribution + reports the impression (TES-1577).
            // The card confirms inline (rule 2: the row's own completion
            // state), so this records without toasting and reports the
            // surface honestly as 'sound_card_inline', not a toast.
            recordPostDownloadConfirmation({
              soundId: sound.id,
              soundTitle: sound.title,
              soundSlug: sound.slug,
              source: dlSource,
              surface: 'sound_card_inline',
            });
          },
          onError: () => { setIsDownloading(false); },
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [isDownloading, downloadDone, sound, downloadFile, previewCompleted],
  );

  return { isDownloading, downloadDone, handleCardDownload };
}
